import { useState } from "react";
import { CaretDown, CaretRight, Lock } from "@phosphor-icons/react";
import { inr, CostDot } from "./CostSummary";

const EDITABLE = ["quantity", "unit_cost", "vendor", "lead_time_weeks", "notes"];

function EditableCell({ item, field, value, onSave, format }) {
  const [editing, setEditing] = useState(false);
  const [val, setVal] = useState(value ?? "");
  if (!editing) {
    return (
      <span className="cursor-text hover:bg-[var(--c-border)]/60 px-1 rounded-[2px] block min-h-[16px]"
        onClick={() => { setVal(value ?? ""); setEditing(true); }}
        data-testid={`edit-${field}-${item.item_number}`}>
        {format ? format(value) : (value ?? "—")}
      </span>
    );
  }
  return (
    <input autoFocus className="w-full h-6 bg-[var(--c-bg)] border border-[var(--c-accent)] text-[var(--c-text)] text-[11px] font-mono rounded-sm px-1 outline-none"
      value={val} onChange={(e) => setVal(e.target.value)}
      data-testid={`edit-input-${field}-${item.item_number}`}
      onBlur={() => { setEditing(false); if (String(val) !== String(value ?? "")) onSave(item.item_number, field, ["quantity", "unit_cost", "lead_time_weeks"].includes(field) ? parseFloat(val) || 0 : val); }}
      onKeyDown={(e) => { if (e.key === "Enter") e.target.blur(); if (e.key === "Escape") setEditing(false); }} />
  );
}

export function BomItemRow({ item, onSave, showCat }) {
  return (
    <tr className="border-b border-[var(--c-border)]/40 hover:bg-[var(--c-border)]/25" data-testid={`bom-row-${item.item_number}`}>
      <td className="py-1.5 px-2 font-mono text-[10px] text-[var(--c-muted)]">{item.item_number}</td>
      <td className="py-1.5 px-2">
        <div className="text-[11px] text-[var(--c-text)]">{item.description}</div>
        <div className="text-[9px] text-[var(--c-muted)] font-mono">{item.material_spec}</div>
      </td>
      <td className="py-1.5 px-2 text-right font-mono text-[11px] text-[var(--c-text)]">
        <EditableCell item={item} field="quantity" value={item.quantity} onSave={onSave} />
      </td>
      <td className="py-1.5 px-2 text-[10px] text-[var(--c-muted)]">{item.unit}</td>
      <td className="py-1.5 px-2 text-right font-mono text-[11px] text-[var(--c-muted)]">{item.total_weight ?? "—"}</td>
      <td className="py-1.5 px-2 text-right font-mono text-[11px] text-[var(--c-text)]">
        <span className="flex items-center justify-end">
          <EditableCell item={item} field="unit_cost" value={item.unit_cost} onSave={onSave} />
          <CostDot source={item.cost_source} />
        </span>
      </td>
      <td className="py-1.5 px-2 text-right font-mono text-[11px] text-[var(--c-text)]" data-testid={`total-cost-${item.item_number}`}>{inr(item.total_cost)}</td>
      <td className="py-1.5 px-2 text-[10px] text-[var(--c-muted)]">
        <EditableCell item={item} field="vendor" value={item.vendor} onSave={onSave} />
      </td>
      <td className="py-1.5 px-2 text-[10px]">
        <span className={item.procurement_type === "bought_out" ? "text-[var(--c-amber)]" : "text-[var(--c-green)]"}>
          {item.procurement_type === "bought_out" ? "Bought-out" : "In-house"}
        </span>
        {item.critical_path && <span className="ml-1 text-[var(--c-red)]" title="Critical path">⚑</span>}
      </td>
    </tr>
  );
}

export default function BomTree({ template, items, categories, onSave }) {
  const [open, setOpen] = useState(() => Object.fromEntries(template.map((t) => [t.id, true])));
  const itemsFor = (cid) => items.filter((i) => i.category_id === cid);

  return (
    <div className="bg-[var(--c-surface)] border border-[var(--c-border)] rounded-lg shadow-sm overflow-hidden" data-testid="bom-tree">
      <table className="w-full">
        <thead>
          <tr className="border-b border-[var(--c-border)] text-left font-mono text-[9px] uppercase tracking-wider text-[var(--c-muted)]">
            <th className="py-2 px-2">Item</th><th className="py-2 px-2">Description</th>
            <th className="py-2 px-2 text-right">Qty</th><th className="py-2 px-2">Unit</th>
            <th className="py-2 px-2 text-right">Wt (kg)</th><th className="py-2 px-2 text-right">Unit Cost</th>
            <th className="py-2 px-2 text-right">Total</th><th className="py-2 px-2">Vendor</th><th className="py-2 px-2">Proc.</th>
          </tr>
        </thead>
        {template.map((cat, ci) => {
          const catItems = itemsFor(cat.id);
          const roll = categories?.[cat.id] || { count: 0, weight: 0, cost: 0 };
          const isOpen = open[cat.id];
          return (
            <tbody key={cat.id}>
              <tr className="bg-[var(--c-bg)] cursor-pointer border-b border-[var(--c-border)]"
                onClick={() => setOpen((o) => ({ ...o, [cat.id]: !isOpen }))}
                data-testid={`bom-cat-${cat.id}`}>
                <td colSpan={9} className="py-2 px-2">
                  <div className="flex items-center gap-2">
                    {isOpen ? <CaretDown size={11} /> : <CaretRight size={11} />}
                    <span className="text-[11px] text-[var(--c-text)] font-medium">{ci + 1}. {cat.name}</span>
                    <span className="text-[10px] text-[var(--c-muted)] font-mono">
                      ({roll.count} items{roll.weight ? `, ${Number(roll.weight).toLocaleString()} kg` : ""}{roll.cost ? `, ${inr(roll.cost)}` : ""})
                    </span>
                    {!catItems.length && <span className="text-[10px] text-[var(--c-muted)]">⏳ Awaiting data</span>}
                  </div>
                </td>
              </tr>
              {isOpen && catItems.map((it) => <BomItemRow key={it.item_number} item={it} onSave={onSave} />)}
            </tbody>
          );
        })}
      </table>
    </div>
  );
}
